import React, { useState, useEffect, useRef } from 'react';
import { getChatbotQuestions, sendChatbotAnswer } from '../../services/chatbot';
import '../../styles/ChatbotWidget.css';

const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" fill="white" fillOpacity="0.2"/>
    <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <circle cx="9" cy="10" r="1.2" fill="white"/>
    <circle cx="15" cy="10" r="1.2" fill="white"/>
    <path d="M12 3v2M9 3.5L10 5.2M15 3.5L14 5.2" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const BotAvatarSmall = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="9" cy="10" r="1.5" fill="white"/>
    <circle cx="15" cy="10" r="1.5" fill="white"/>
  </svg>
);

const ChatbotWidget = ({ isOpen, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const getTime = () =>
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  useEffect(() => {
    if (isOpen) {
      loadQuestions();
      setMessages([{
        type: 'bot',
        text: "Hello and welcome! 👋\nI'm your virtual assistant.\nPlease select one of the options below.",
        timestamp: getTime()
      }]);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadQuestions = async () => {
    try {
      const response = await getChatbotQuestions();
      const list = response?.data ?? response ?? [];
      setQuestions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load chatbot questions:', error);
    }
  };

  const handleQuestionClick = async (question) => {
    if (loading) return;
    const questionText = question.question || question.text;
    const code = question.code;

    setMessages(prev => [...prev, { type: 'user', text: questionText, timestamp: getTime() }]);
    setLoading(true);

    try {
      const response = await sendChatbotAnswer(code);
      const answer =
        response?.data?.answer ||
        response?.answer ||
        "Thank you for your question. Our team will get back to you soon.";
      setMessages(prev => [...prev, { type: 'bot', text: answer, timestamp: getTime() }]);
    } catch (error) {
      console.error('Failed to send answer:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Sorry, I encountered an error. Please try again.",
        timestamp: getTime()
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-widget">
      {/* ── Header ── */}
      <div className="chatbot-header">
        <div className="chatbot-header-avatar"><BotIcon /></div>
        <div className="chatbot-header-info">
          <h3>Support Center</h3>
          <div className="chatbot-header-status">
            <div className="chatbot-header-status-dot" />
            <span>Online · typically replies instantly</span>
          </div>
        </div>
        <button className="chatbot-close-btn" onClick={onClose} aria-label="Close chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* ── Messages ── */}
      <div className="chatbot-messages">
        {messages.map((message, index) => (
          <div key={index} className={`chatbot-message ${message.type}`}>
            {message.type === 'bot' && (
              <div className="message-avatar"><BotAvatarSmall /></div>
            )}
            <div className="message-content">
              <div className="message-bubble">
                {message.text.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              <span className="message-time">{message.timestamp}</span>
            </div>
          </div>
        ))}

        {loading && (
          <div className="chatbot-message bot">
            <div className="message-avatar"><BotAvatarSmall /></div>
            <div className="message-content">
              <div className="message-bubble typing">
                <span/><span/><span/>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Quick Questions ── */}
      {questions.length > 0 && (
        <div className="chatbot-quick-questions">
          <div className="chatbot-quick-questions-label">Quick Questions</div>
          {questions.slice(0, 4).map((question, index) => (
            <button
              key={question.code || index}
              className="quick-question-btn"
              onClick={() => handleQuestionClick(question)}
              disabled={loading}
            >
              {question.question}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;